import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Box, Typography } from "@mui/material";
import { DataTable } from "@outpave/ds";
import type { DataColumn, RowAction } from "@outpave/ds";
import { HugeiconsIcon } from "@hugeicons/react";
import { Delete02Icon, Edit04Icon } from "@hugeicons-pro/core-stroke-rounded";
import { GET_CARD_PRESETS } from "../../../graphql/cardPresets";

import { DEFAULT_PAGINATION_SIZE } from "../../common/Constants";
import { formatCash } from "../../../utils/formatCash";
import { useFeatureEntitlements } from "@frontegg/react";
import RenderCardSettingPolicy from "components/common/cards/RenderCardSettingPolicy";
import FancyHoverComponent from "../../common/FancyHover";
import { titleCase } from "title-case";
import PolicyRequired from "../../../enums/PolicyRequired";
import useInfiniteScroll from "hooks/useInfiniteScroll";
import {
  getRestrictionListLabels,
  restrictionEntryLabel,
} from "utils/cards/cardFormUtils";

interface CardPresetNode {
  id: string;
  presetName: string;
  currency?: string;
  transactionLimit?: number | null;
  dailyLimit?: number | null;
  weeklyLimit?: number | null;
  monthlyLimit?: number | null;
  totalLimit?: number | null;
  expenseCategoryData?: { name: string } | null;
  allocationData?: { name: string; allocationCode: string } | null;
  receiptPolicy: PolicyRequired;
  allocationPolicy: PolicyRequired;
  cancellationTimeFrame?: {
    timeFrameValue?: number | null;
    timeFrameFrequency?: string | null;
  } | null;
  allowedMerchants?: { name: string | null }[] | null;
  allowedCategories?: { name: string }[] | null;
  blockedMerchants?: { name: string | null }[] | null;
  blockedCategories?: { name: string }[] | null;
}

interface CardPresetsQueryResult {
  cardPresets?: { edges?: { node: CardPresetNode }[] };
}

interface CreditSettingsTableProps {
  refreshPresets: number;
  handleEditButton: (id: string) => void;
  handleDeleteButton: (id: string) => void;
}

interface Data {
  id: string;
  presetName: string;
  transactionLimit: string;
  dailyLimit: string;
  weeklyLimit: string;
  monthlyLimit: string;
  totalLimit: string;
  expenseCategory: string;
  allocations: string;
  receiptPolicy: PolicyRequired;
  allocationPolicy: PolicyRequired;
  cancelationTimeFrame: string;
  combinedCategoryAndMerchant: { name: string; type: string }[];
}

const LIMIT_ROWS = [
  { field: "transactionLimit", label: "Transaction" },
  { field: "dailyLimit", label: "Daily" },
  { field: "weeklyLimit", label: "Weekly" },
  { field: "monthlyLimit", label: "Monthly" },
  { field: "totalLimit", label: "Total" },
] as const;

const defaultPaginationVariables = {
  first: DEFAULT_PAGINATION_SIZE,
  after: undefined,
};

const formatLimit = (limit?: number | null, currency?: string): string =>
  limit === undefined || limit === null
    ? "-"
    : formatCash(limit, currency) || "-";

const formatTimeFrame = (
  timeFrameValue: number,
  timeFrameFrequency: string,
): string => {
  if (!timeFrameFrequency) return "-";
  const capitalizedUnit =
    timeFrameFrequency.charAt(0).toUpperCase() + timeFrameFrequency.slice(1);
  return timeFrameValue > 1
    ? `${timeFrameValue} ${capitalizedUnit}s`
    : `${timeFrameValue} ${capitalizedUnit}`;
};

/**
 * The preset's restrictions as one list. Allowed entries come first, then
 * blocked ones; `type` carries the direction so the column can label a mixed
 * list without inspecting the names.
 */
const combineRestrictions = (
  node: CardPresetNode,
): { name: string; type: string }[] => {
  const allowed =
    node.allowedMerchants && node.allowedMerchants.length > 0
      ? node.allowedMerchants.map((merchant) => ({
          name: merchant?.name ? titleCase(merchant.name.toLowerCase()) : "",
          type: "merchant",
        }))
      : node.allowedCategories && node.allowedCategories.length > 0
        ? node.allowedCategories.map((category) => ({
            name: category.name,
            type: "category",
          }))
        : [];
  const blocked = [
    ...(node.blockedMerchants ?? []).map((merchant) => ({
      name: merchant?.name ? titleCase(merchant.name.toLowerCase()) : "",
      type: "blocked",
    })),
    ...(node.blockedCategories ?? []).map((category) => ({
      name: category.name,
      type: "blockedCategory",
    })),
  ];
  const combined = [...allowed, ...blocked];
  return combined.length ? combined : [{ name: "-", type: "none" }];
};

const extractCardPresets = (data: CardPresetsQueryResult): Data[] =>
  (data.cardPresets?.edges ?? []).map(({ node }) => ({
    id: node.id,
    presetName: node.presetName || "-",
    transactionLimit: formatLimit(node.transactionLimit, node.currency),
    dailyLimit: formatLimit(node.dailyLimit, node.currency),
    weeklyLimit: formatLimit(node.weeklyLimit, node.currency),
    monthlyLimit: formatLimit(node.monthlyLimit, node.currency),
    totalLimit: formatLimit(node.totalLimit, node.currency),
    expenseCategory: node.expenseCategoryData
      ? node.expenseCategoryData.name
      : "-",
    allocations: node.allocationData
      ? `${node.allocationData?.name} - ${node.allocationData?.allocationCode}`
      : "-",
    receiptPolicy: node.receiptPolicy,
    allocationPolicy: node.allocationPolicy,
    cancelationTimeFrame:
      node.cancellationTimeFrame && node.cancellationTimeFrame.timeFrameValue
        ? formatTimeFrame(
            node.cancellationTimeFrame.timeFrameValue,
            node.cancellationTimeFrame.timeFrameFrequency ?? "",
          )
        : "-",
    combinedCategoryAndMerchant: combineRestrictions(node),
  }));

const CreditSettingsTable: React.FC<CreditSettingsTableProps> = (props) => {
  const { refreshPresets, handleEditButton, handleDeleteButton } = props;

  const { isEntitled: featureCardTypes } =
    useFeatureEntitlements("feature_card_types");
  const { isEntitled: featureMerchantLimits } = useFeatureEntitlements(
    "feature_merchant_limits",
  );
  const { isEntitled: featureAllocations } = useFeatureEntitlements(
    "feature_allocations",
  );

  const { t } = useTranslation();

  const { dataArray, displayLoading, handleScroll, isInfiniteScrollFetching } =
    useInfiniteScroll({
      query: GET_CARD_PRESETS,
      initialVariables: defaultPaginationVariables,
      extractData: extractCardPresets,
      refetchIndex: refreshPresets,
    });

  const columns: DataColumn<Data>[] = useMemo(
    () => [
      {
        id: "presetName",
        header: t("Preset name"),
        cell: (preset) => preset.presetName,
      },
      {
        id: "limits",
        header: t("Limits"),
        cell: (preset) => (
          <table>
            <tbody>
              {LIMIT_ROWS.map(({ field, label }) => (
                <tr key={field}>
                  <td>
                    <Typography variant="body1">{t(label)}:</Typography>
                  </td>
                  <td style={{ paddingLeft: "var(--space-3)" }}>
                    <Typography variant="body1">{preset[field]}</Typography>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ),
      },
      ...(featureCardTypes
        ? [
            {
              id: "expenseCategory",
              header: t("Expense Category"),
              cell: (preset: Data) => preset.expenseCategory,
            },
            ...(featureAllocations
              ? [
                  {
                    id: "allocations",
                    header: t("Allocations"),
                    cell: (preset: Data) => preset.allocations,
                  },
                ]
              : []),
            {
              id: "policies",
              header: t("Policy"),
              cell: (preset: Data) =>
                featureAllocations ? (
                  <RenderCardSettingPolicy
                    policy={preset.allocationPolicy}
                    secondPolicy={preset.receiptPolicy}
                  />
                ) : (
                  <RenderCardSettingPolicy policy={preset.receiptPolicy} />
                ),
            },
            {
              id: "cancelationTimeFrame",
              header: t("Cancel Period"),
              cell: (preset: Data) => preset.cancelationTimeFrame,
            },
          ]
        : []),
      ...(featureMerchantLimits
        ? [
            {
              id: "allowedMerchants",
              header: t("Merchants / Categories"),
              cell: (preset: Data) =>
                preset.combinedCategoryAndMerchant?.length < 4 ? (
                  <div>
                    {preset.combinedCategoryAndMerchant.map((item, index) => (
                      <div key={`${index}-${item.name}`}>
                        {restrictionEntryLabel(item, t)}
                      </div>
                    ))}
                  </div>
                ) : (
                  <FancyHoverComponent
                    title={
                      getRestrictionListLabels(
                        preset.combinedCategoryAndMerchant,
                        t,
                      ).title
                    }
                    data={preset.combinedCategoryAndMerchant.map((item) =>
                      restrictionEntryLabel(item, t),
                    )}
                  >
                    <Typography variant="subtitle2">
                      {
                        getRestrictionListLabels(
                          preset.combinedCategoryAndMerchant,
                          t,
                        ).summary
                      }
                    </Typography>
                  </FancyHoverComponent>
                ),
            },
          ]
        : []),
    ],
    [t, featureCardTypes, featureAllocations, featureMerchantLimits],
  );

  const rowActions = (preset: Data): RowAction[] => [
    {
      label: t("Edit Configuration"),
      icon: <HugeiconsIcon icon={Edit04Icon} size={16} />,
      onClick: () => handleEditButton(preset.id),
    },
    {
      label: t("Remove Configuration"),
      icon: <HugeiconsIcon icon={Delete02Icon} size={16} />,
      onClick: () => handleDeleteButton(preset.id),
    },
  ];

  return (
    <Box sx={{ height: "100%" }}>
      <DataTable<Data>
        columns={columns}
        rows={dataArray}
        getRowKey={(preset) => preset.id}
        loading={displayLoading}
        isFetchingMore={isInfiniteScrollFetching}
        onRowClick={(preset) => handleEditButton(preset.id)}
        onScroll={handleScroll}
        emptyState={t("No presets found")}
        rowActions={rowActions}
        rowActionsLabel={t("RowActions")}
        loadingMoreText={t("Loading more presets")}
        stickyHeader
      />
    </Box>
  );
};

export default CreditSettingsTable;
